import { useState, useEffect } from 'react';

export interface ResourceFormData {
  title: string;
  resourceType: string;
  subject: string;
  level: string;
  board: string;
  visibility: string;
  description: string;
  tags: string;
}

interface ResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ResourceFormData) => void;
  initialData?: Partial<ResourceFormData> & { id?: number };
  mode: 'create' | 'edit';
}

const defaultForm: ResourceFormData = {
  title: '',
  resourceType: 'PDF',
  subject: '',
  level: 'GCSE',
  board: 'AQA',
  visibility: 'Students Only',
  description: '',
  tags: '',
};

const RESOURCE_TYPES = [
  { label: 'PDF', icon: 'ri-file-pdf-line', color: 'text-red-500 bg-red-50' },
  { label: 'DOCX', icon: 'ri-file-word-line', color: 'text-blue-500 bg-blue-50' },
  { label: 'PPT', icon: 'ri-file-ppt-line', color: 'text-orange-500 bg-orange-50' },
  { label: 'Spreadsheet', icon: 'ri-file-excel-line', color: 'text-emerald-600 bg-emerald-50' },
  { label: 'Video', icon: 'ri-video-line', color: 'text-purple-500 bg-purple-50' },
];

const LEVELS = ['GCSE', 'A-Level', 'Both'];
const BOARDS = ['AQA', 'OCR', 'Edexcel', 'WJEC', 'Cambridge', 'All Boards'];
const VISIBILITY_OPTIONS = [
  { value: 'Public', icon: 'ri-global-line', desc: 'Anyone can see this' },
  { value: 'Students Only', icon: 'ri-group-line', desc: 'Only your students' },
  { value: 'Private', icon: 'ri-lock-line', desc: 'Only you' },
];

export default function ResourceModal({ isOpen, onClose, onSubmit, initialData, mode }: ResourceModalProps) {
  const [form, setForm] = useState<ResourceFormData>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof ResourceFormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(initialData ? { ...defaultForm, ...initialData } : defaultForm);
      setErrors({});
      setSubmitted(false);
    }
  }, [isOpen, initialData]);

  const set = (key: keyof ResourceFormData, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ResourceFormData, string>> = {};
    if (!form.title.trim()) newErrors.title = 'Resource title is required';
    if (!form.subject.trim()) newErrors.subject = 'Subject / topic is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitted(true);
    setTimeout(() => {
      onSubmit(form);
      onClose();
    }, 800);
  };

  const selectedTypeInfo = RESOURCE_TYPES.find((t) => t.label === form.resourceType);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg">
              <i className={`${mode === 'create' ? 'ri-upload-2-line' : 'ri-edit-line'} text-emerald-700 text-lg`}></i>
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
                {mode === 'create' ? 'Upload New Resource' : 'Edit Resource'}
              </h2>
              <p className="text-xs text-slate-400">
                {mode === 'create' ? 'Add a resource for your students' : 'Update the resource details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-5">
          {/* Resource Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Resource Title <span className="text-red-400">*</span>
            </label>
            <input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. AQA GCSE Revision Notes – Unit 1"
              className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.title ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>

          {/* Resource Type */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Resource Type</label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {RESOURCE_TYPES.map((type) => (
                <button
                  key={type.label}
                  onClick={() => set('resourceType', type.label)}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all cursor-pointer ${form.resourceType === type.label
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-slate-100 bg-white hover:border-slate-300'
                    }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${type.color}`}>
                    <i className={`${type.icon} text-base`}></i>
                  </div>
                  <span className={`text-xs font-semibold ${form.resourceType === type.label ? 'text-emerald-700' : 'text-slate-500'}`}>
                    {type.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Subject / Topic */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Subject / Topic <span className="text-red-400">*</span>
            </label>
            <input
              value={form.subject}
              onChange={(e) => set('subject', e.target.value)}
              placeholder="e.g. Algorithms, Binary, Python OOP, Data Structures"
              className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none transition-colors ${errors.subject ? 'border-red-300 focus:border-red-400' : 'border-slate-200 focus:border-emerald-500'}`}
            />
            {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
          </div>

          {/* Level & Board */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Level</label>
              <div className="flex gap-2">
                {LEVELS.map((l) => (
                  <button
                    key={l}
                    onClick={() => set('level', l)}
                    className={`flex-1 text-xs font-medium py-2 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${form.level === l
                        ? 'bg-emerald-700 text-white border-emerald-700'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                      }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Exam Board</label>
              <select
                value={form.board}
                onChange={(e) => set('board', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 bg-white"
              >
                {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Visibility</label>
            <div className="grid grid-cols-3 gap-2">
              {VISIBILITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => set('visibility', opt.value)}
                  className={`flex flex-col items-start gap-1 p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${form.visibility === opt.value
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-slate-100 bg-white hover:border-slate-300'
                    }`}
                >
                  <div className="flex items-center gap-1.5">
                    <i className={`${opt.icon} text-sm ${form.visibility === opt.value ? 'text-emerald-700' : 'text-slate-400'}`}></i>
                    <span className={`text-xs font-semibold ${form.visibility === opt.value ? 'text-emerald-700' : 'text-slate-600'}`}>
                      {opt.value}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-tight">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* File uploads handled via file picker; external link option removed */}

          {/* Tags */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Tags
              <span className="ml-1 text-slate-400 font-normal normal-case tracking-normal">(comma separated)</span>
            </label>
            <input
              value={form.tags}
              onChange={(e) => set('tags', e.target.value)}
              placeholder="e.g. algorithms, binary, revision, paper 1"
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500"
            />
            {form.tags && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.tags.split(',').map((tag) => tag.trim()).filter(Boolean).map((tag) => (
                  <span key={tag} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description / Notes</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Briefly describe what this resource covers and how students should use it..."
              rows={3}
              maxLength={500}
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 resize-none"
            />
            <p className="text-xs text-slate-400 text-right mt-1">{form.description.length}/500</p>
          </div>

          {/* Summary preview */}
          {form.title && form.subject && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">Resource Summary</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-600">
                <span>
                  <i className="ri-file-line mr-1.5 text-emerald-600"></i>
                  {form.title}
                </span>
                <span>
                  <i className="ri-price-tag-3-line mr-1.5 text-emerald-600"></i>
                  {form.resourceType}
                </span>
                <span>
                  <i className="ri-book-open-line mr-1.5 text-emerald-600"></i>
                  {form.subject}
                </span>
                <span>
                  <i className="ri-award-line mr-1.5 text-emerald-600"></i>
                  {form.level} · {form.board}
                </span>
                <span>
                  <i className="ri-eye-line mr-1.5 text-emerald-600"></i>
                  {form.visibility}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitted}
            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {submitted ? (
              <><i className="ri-check-line"></i> {mode === 'create' ? 'Uploaded!' : 'Updated!'}</>
            ) : (
              <><i className={mode === 'create' ? 'ri-upload-2-line' : 'ri-save-line'}></i> {mode === 'create' ? 'Upload Resource' : 'Save Changes'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
